import { ConnectorFlowDTO, PrepareConnectorFlowStepResponse } from '@/models/connectors';
import { RJSFSchema } from '@rjsf/utils';

export type PreparedFlowReactForm = {
  schema: RJSFSchema;
  uiSchema?: Record<string, unknown>;
  formId?: string;
  title?: string;
  description?: string;
};

type FlowElement = {
  reactJsonSchemaForm?: PreparedFlowReactForm;
};

type PreparedFlowElements = {
  params?: Record<string, FlowElement> & FlowElement;
  flow?: FlowElement;
  mappings?: { reactJsonSchemaForm?: { schema: RJSFSchema } };
};

const getReactFormFromElement = (element: FlowElement | undefined): PreparedFlowReactForm | null => {
  const form = element?.reactJsonSchemaForm;
  return form?.schema ? form : null;
};

const getPreparedFlowElements = (preparedFlow: ConnectorFlowDTO | undefined): PreparedFlowElements | undefined => {
  if (!preparedFlow) return undefined;

  const configuration = preparedFlow.configuration;
  const currentVersion = configuration?.['currentVersion'];
  const configurations = configuration?.['configurations'] || [];
  let elements: PreparedFlowElements | undefined;

  if (currentVersion && configurations.length > 0) {
    elements = configurations.find((c: { version: string }) => c.version === currentVersion)?.['elements'];
  }

  return elements || configurations[0]?.['elements'] || configuration?.['elements'];
};

export const getEntityFormFromPreparedFlow = (preparedFlow: ConnectorFlowDTO | undefined): PreparedFlowReactForm | null => {
  const elements = getPreparedFlowElements(preparedFlow);
  const params = elements?.params;

  return (
    getReactFormFromElement(params?.['step1']) ||
    getReactFormFromElement(params) ||
    getReactFormFromElement(elements?.flow) ||
    null
  );
};

export const collectSchemaDefaults = (schema: RJSFSchema | undefined): Record<string, unknown> => {
  const data: Record<string, unknown> = {};
  const schemaWithDeps = schema as {
    properties?: Record<string, { default?: unknown }>;
    dependencies?: Record<
      string,
      {
        oneOf?: Array<{
          properties?: Record<string, { default?: unknown; enum?: unknown[] }>;
        }>;
      }
    >;
  };

  const properties = schemaWithDeps?.properties || {};

  for (const [key, property] of Object.entries(properties)) {
    if (property?.default !== undefined) {
      data[key] = property.default;
    }
  }

  for (const [dependencyKey, dependency] of Object.entries(schemaWithDeps?.dependencies || {})) {
    const dependencyValue = data[dependencyKey];
    if (dependencyValue === undefined) continue;

    const matchingBranch = dependency.oneOf?.find((branch) => branch.properties?.[dependencyKey]?.enum?.includes(dependencyValue));
    if (!matchingBranch?.properties) continue;

    for (const [key, property] of Object.entries(matchingBranch.properties)) {
      if (key !== dependencyKey && property?.default !== undefined && data[key] === undefined) {
        data[key] = property.default;
      }
    }
  }

  return data;
};

export const isMultiStepConnectorFlow = (flow: ConnectorFlowDTO | undefined): boolean => {
  const params = getPreparedFlowElements(flow)?.params;
  if (!params) return false;
  return Object.keys(params).some((key) => /^step\d+$/.test(key));
};

export const getStaticEntityFormFromFlow = (flow: ConnectorFlowDTO | undefined): PreparedFlowReactForm | null => {
  return getEntityFormFromPreparedFlow(flow);
};

export const getEntityFormFromPrepareStepResponse = (
  response: PrepareConnectorFlowStepResponse,
  stepKey = 'step1'
): PreparedFlowReactForm | null => {
  const form =
    response.configuration?.elements?.params?.[stepKey]?.reactJsonSchemaForm ||
    response.configuration?.elements?.flow?.reactJsonSchemaForm ||
    response.reactJsonSchemaForm ||
    response.configuration?.reactJsonSchemaForm;

  return form?.schema ? (form as PreparedFlowReactForm) : null;
};

export const getSortedParamStepKeys = (preparedFlow: ConnectorFlowDTO | undefined): string[] => {
  const elements = getPreparedFlowElements(preparedFlow);
  const params = elements?.params || {};
  return Object.keys(params)
    .filter((key) => /^step\d+$/.test(key))
    .sort((a, b) => parseInt(a.replace('step', ''), 10) - parseInt(b.replace('step', ''), 10));
};

/** B5: mapping prepare step is the second params step (step2) when present. */
export const getMappingPrepareStepId = (preparedFlow: ConnectorFlowDTO | undefined): string | null => {
  const stepKeys = getSortedParamStepKeys(preparedFlow);
  if (stepKeys.length >= 2) return stepKeys[1];
  if (stepKeys.length === 1) return 'step2';
  return 'step2';
};

const getHeadersFromSchema = (schema: RJSFSchema | undefined): string[] => {
  const properties = (schema as { properties?: Record<string, unknown> })?.properties || {};
  return Object.keys(properties);
};

export const extractMappingHeadersFromPrepareStepResponse = (
  response: PrepareConnectorFlowStepResponse,
  stepKey: string
): string[] => {
  const mappingsSchema =
    response.configuration?.elements?.mappings?.reactJsonSchemaForm?.schema ||
    response.configuration?.mappings?.reactJsonSchemaForm?.schema ||
    response.mappings?.reactJsonSchemaForm?.schema;

  if (mappingsSchema) {
    const fromMappings = getHeadersFromSchema(mappingsSchema);
    if (fromMappings.length > 0) return fromMappings;
  }

  const stepFormSchema =
    response.configuration?.elements?.params?.[stepKey]?.reactJsonSchemaForm?.schema ||
    response.reactJsonSchemaForm?.schema ||
    response.configuration?.reactJsonSchemaForm?.schema;

  return getHeadersFromSchema(stepFormSchema);
};
